import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { CheckCircle2, XCircle, Mail, Phone, MapPin, Briefcase } from 'lucide-react';
import BadgeChips from './BadgeChips';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const KIND_LABEL = { necs_admin: 'NECS Admin', necs_staff: 'NECS Staff', property_manager: 'Property Manager' };
const STATUS_STYLE = {
  pending_admin_review: { bg: '#fef3c7', color: '#92400e', label: 'Pending' },
  approved: { bg: '#dcfce7', color: '#166534', label: 'Approved' },
  declined: { bg: '#fee2e2', color: '#991b1b', label: 'Declined' },
};

export default function AccessRequestsTab() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState({});
  const [filter, setFilter] = useState('pending_admin_review');

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/access-requests`, { credentials: 'include' });
      if (!res.ok) throw new Error('Failed to load');
      const data = await res.json();
      setRequests(data.requests || []);
    } catch (e) {
      toast.error('Could not load access requests.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const act = async (req, action) => {
    setActing((s) => ({ ...s, [req.request_id]: action }));
    try {
      const res = await fetch(`${BACKEND_URL}/api/access-requests/${req.request_id}/${action}`, {
        method: 'POST', credentials: 'include',
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Action failed');
      if (action === 'approve') {
        toast.success(`Approved · ${req.email} · employee ${data.employee_number}`);
      } else {
        toast.success(`Declined · ${req.email}`);
      }
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActing((s) => { const c = { ...s }; delete c[req.request_id]; return c; });
    }
  };

  const filtered = requests.filter((r) => filter === 'all' || r.status === filter);

  return (
    <div className="space-y-4" data-testid="access-requests-tab">
      <div className="flex flex-wrap items-center gap-2">
        {['pending_admin_review', 'approved', 'declined', 'all'].map((k) => (
          <button
            key={k}
            type="button"
            onClick={() => setFilter(k)}
            className="text-xs font-semibold px-3 py-1.5 rounded-full transition"
            style={{
              backgroundColor: filter === k ? '#C9A962' : 'transparent',
              color: filter === k ? '#0a0a0a' : '#e5e5e5',
              border: filter === k ? '1px solid #C9A962' : '1px solid #3f3f46',
            }}
            data-testid={`access-filter-${k}`}
          >
            {STATUS_STYLE[k]?.label || 'All'} ({requests.filter((r) => k === 'all' || r.status === k).length})
          </button>
        ))}
        <Button
          type="button"
          onClick={load}
          variant="outline"
          className="ml-auto text-xs"
          data-testid="access-refresh"
        >
          Refresh
        </Button>
      </div>

      {loading ? (
        <p className="text-zinc-400 text-sm" data-testid="access-loading">Loading...</p>
      ) : filtered.length === 0 ? (
        <Card className="p-6 bg-zinc-900 border-zinc-800" data-testid="access-empty">
          <p className="text-zinc-400 text-sm text-center">No requests in this status.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {filtered.map((req) => {
            const st = STATUS_STYLE[req.status] || STATUS_STYLE.pending_admin_review;
            const isPending = req.status === 'pending_admin_review';
            const busy = acting[req.request_id];
            return (
              <Card
                key={req.request_id}
                className="p-5 bg-zinc-900 border-zinc-800"
                data-testid={`access-request-${req.request_id}`}
              >
                <div className="flex flex-wrap items-start justify-between gap-4">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-base font-bold text-white">
                        {req.first_name} {req.last_name}
                      </h3>
                      <Badge style={{ backgroundColor: st.bg, color: st.color }}>{st.label}</Badge>
                      <Badge variant="outline" className="text-amber-300 border-amber-700">
                        {KIND_LABEL[req.kind] || req.kind}
                      </Badge>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-2 text-xs text-zinc-300">
                      <Row icon={Mail} value={req.email} />
                      <Row icon={Phone} value={req.phone || '—'} />
                      <Row icon={Briefcase} value={req.position || '—'} />
                      <Row icon={MapPin} value={[req.department, req.region, req.state].filter(Boolean).join(' · ') || '—'} />
                    </div>
                    {/* Structured badge surfaced as color chips so admins can scan dozens at a glance */}
                    <div className="mt-3" data-testid={`access-badge-chips-${req.request_id}`}>
                      <BadgeChips badge={req.badge_number} />
                    </div>
                    <p className="text-[10px] text-zinc-500 mt-2">
                      Requested {new Date(req.created_at).toLocaleString()}
                    </p>
                  </div>
                  {isPending && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => act(req, 'approve')}
                        disabled={!!busy}
                        className="font-bold"
                        style={{ backgroundColor: '#16a34a', color: 'white' }}
                        data-testid={`access-approve-${req.request_id}`}
                      >
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        {busy === 'approve' ? 'Approving…' : 'Approve'}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => act(req, 'decline')}
                        disabled={!!busy}
                        variant="outline"
                        className="font-bold"
                        data-testid={`access-decline-${req.request_id}`}
                      >
                        <XCircle className="h-4 w-4 mr-1" />
                        {busy === 'decline' ? 'Declining…' : 'Decline'}
                      </Button>
                    </div>
                  )}
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Row({ icon: Icon, value }) {
  return (
    <span className="flex items-center gap-1.5 min-w-0">
      <Icon className="h-3.5 w-3.5 text-zinc-500 flex-shrink-0" />
      <span className="truncate">{value}</span>
    </span>
  );
}
